import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Category, type CounterpartyPage, type MemoRule } from "../../api";
import { Modal } from "../../components/Modal";
import { Btn, Field, Input, Notice, Select } from "../../components/ui";
import { useEntities } from "../../entity";
import { useMe } from "../../session";

const MATCH = { igual: "For igual a", inicia: "Começar com", contem: "Contiver" } as const;

/** Regras de memorização (DEC-051): estrutura de cadastro. */
export function MemoRules() {
  const { me } = useMe();
  const { entities } = useEntities();
  const realm = me.user.realm;
  const [rules, setRules] = useState<MemoRule[] | null>(null);
  const [cats, setCats] = useState<Category[]>([]);
  const [cps, setCps] = useState<CounterpartyPage["items"]>([]);
  const [form, setForm] = useState<MemoRule | "new" | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const canWrite = entities.some((e) => e.isOwner || e.permission === "total");

  const load = useCallback(async () => {
    try {
      const [r, c, p] = await Promise.all([
        api<MemoRule[]>("GET", `/api/${realm}/memo-rules`),
        api<Category[]>("GET", `/api/${realm}/categories?status=ativa`),
        api<CounterpartyPage>("GET", `/api/${realm}/counterparties?status=ativa&limit=200`),
      ]);
      setRules(r); setCats(c); setCps(p.items);
    } catch (e) { setMsg({ kind: "error", text: errMsg(e) }); }
  }, [realm]);
  useEffect(() => { void load(); }, [load]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); setMsg({ kind: "ok", text: ok }); setForm(null); await load(); }
    catch (e) { setMsg({ kind: "error", text: errMsg(e) }); await load(); }
  }
  const scope = (r: MemoRule) => r.entityId ? entities.find((e) => e.id === r.entityId)?.name ?? "Empresa" : realm === "pj" ? "Todas as empresas" : "Geral";

  return (
    <div className="card" data-memo>
      <h3 style={{ fontSize: 16, marginBottom: 4 }}>Memorização de transações</h3>
      <p className="muted" style={{ marginTop: 0 }}>Regras por descrição que, nas próximas entregas, sugerirão categoria e beneficiário/pagador ao registrar lançamentos. Elas nunca repetem valores nem datas, e você sempre poderá aceitar, alterar ou ignorar a sugestão.</p>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {canWrite && <div style={{ marginBottom: 12 }}><Btn onClick={() => { setMsg(null); setForm("new"); }}>+ Nova regra</Btn></div>}
      {rules && rules.length === 0 && <p className="muted" data-empty>Nenhuma regra cadastrada.</p>}
      {rules && rules.length > 0 && (
        <div className="table-card"><table>
          <thead><tr><th>Quando a descrição…</th><th>Sugerir</th><th>Vale para</th><th /></tr></thead>
          <tbody>{rules.map((r) => (
            <tr key={r.id} className={r.status === "inativa" ? "inactive" : ""} data-rule={r.pattern}>
              <td>{MATCH[r.matchType]} <strong>“{r.pattern}”</strong></td>
              <td>{[r.categoryName, r.counterpartyName].filter(Boolean).join(" · ")}</td>
              <td>{scope(r)}{r.status === "inativa" && <> <span className="pill">Inativa</span></>}</td>
              <td className="num">{canWrite && <>
                {r.status === "ativa" && <button className="link" onClick={() => { setMsg(null); setForm(r); }}>Editar</button>}
                {r.status === "ativa"
                  ? <button className="link danger" onClick={() => void act(() => api("POST", `/api/${realm}/memo-rules/${r.id}/inactivate`, { version: r.version }), "Regra inativada.")}>Inativar</button>
                  : <button className="link" onClick={() => void act(() => api("POST", `/api/${realm}/memo-rules/${r.id}/reactivate`, { version: r.version }), "Regra reativada.")}>Reativar</button>}
              </>}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      {form && <RuleModal initial={form === "new" ? undefined : form} cats={cats} cps={cps} onClose={() => setForm(null)}
        onSave={(b) => form === "new"
          ? act(() => api("POST", `/api/${realm}/memo-rules`, b, newIdemKey()), "Regra criada.")
          : act(() => api("PATCH", `/api/${realm}/memo-rules/${form.id}`, { version: form.version, matchType: b.matchType, pattern: b.pattern, categoryId: b.categoryId, counterpartyId: b.counterpartyId }), "Regra atualizada.")} />}
    </div>
  );
}

function RuleModal({ initial, cats, cps, onSave, onClose }: {
  initial?: MemoRule; cats: Category[]; cps: CounterpartyPage["items"];
  onSave: (b: { entityId: string | null; matchType: string; pattern: string; categoryId: string | null; counterpartyId: string | null }) => Promise<void>; onClose: () => void;
}) {
  const { me } = useMe();
  const { entities } = useEntities();
  const [matchType, setMatchType] = useState<string>(initial?.matchType ?? "contem");
  const [pattern, setPattern] = useState(initial?.pattern ?? "");
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? "");
  const [counterpartyId, setCounterpartyId] = useState(initial?.counterpartyId ?? "");
  const [entityId, setEntityId] = useState(initial?.entityId ?? "");
  const [busy, setBusy] = useState(false);
  const byId = new Map(cats.map((c) => [c.id, c]));
  const path = (c: Category): string => (c.parentId && byId.get(c.parentId) ? `${path(byId.get(c.parentId)!)} › ` : "") + c.name;
  const leaves = cats.filter((c) => c.level >= 2 && !c.systemKey).sort((a, b) => path(a).localeCompare(path(b), "pt-BR"));
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try { await onSave({ entityId: entityId || null, matchType, pattern: pattern.trim(), categoryId: categoryId || null, counterpartyId: counterpartyId || null }); } finally { setBusy(false); }
  }
  return (
    <Modal title={initial ? "Editar regra" : "Nova regra"} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <Field label="Quando a descrição…">
          <Select value={matchType} onChange={(e) => setMatchType(e.target.value)} name="ruleMatch">{Object.entries(MATCH).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>
        </Field>
        <Field label="Texto"><Input value={pattern} onChange={(e) => setPattern(e.target.value)} maxLength={200} required name="rulePattern" /></Field>
        <Field label="Sugerir categoria">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} name="ruleCategory"><option value="">—</option>{leaves.map((c) => <option key={c.id} value={c.id}>{path(c)}</option>)}</Select>
        </Field>
        <Field label="Sugerir beneficiário/pagador">
          <Select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} name="ruleCounterparty"><option value="">—</option>{cps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
        </Field>
        {me.user.realm === "pj" && !initial && (
          <Field label="Vale para">
            <Select value={entityId} onChange={(e) => setEntityId(e.target.value)} name="ruleEntity"><option value="">Todas as empresas autorizadas</option>{entities.filter((e) => e.status === "ativa").map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
          </Field>
        )}
        <div className="row"><button type="submit" className="btn primary" disabled={busy || pattern.trim().length < 2 || (!categoryId && !counterpartyId)}>{busy ? "Salvando…" : "Salvar"}</button><Btn variant="ghost" onClick={onClose}>Cancelar</Btn></div>
      </form>
    </Modal>
  );
}
